import { writable, get } from 'svelte/store';
import { api } from '../api/client';
import type { Group } from '../api/types';
import { currentUser } from './auth';
import { websocket } from './websocket';

const SELECTED_KEY = 'selected_group';

function createGroupsStore() {
  const { subscribe, set: store } = writable<Group[]>([]);
  // A tab managing a child keeps its own selection, apart from the parent's
  // groups (spec 001).
  const remember = !api.acting;
  const selected = writable<string | null>(remember ? localStorage.getItem(SELECTED_KEY) : null);
  // loaded is set once the list was read, so an empty list means no groups
  // rather than not yet known (spec 001).
  const loaded = writable(false);
  // listening are the group topics the store follows, by group ID, so a
  // deleted group leaves every page at once (spec 001 FR-109). A tab
  // managing a child has no WebSocket.
  const listening = new Map<string, () => void>();
  function set(list: Group[]) {
    // An unchanged list is not stored again, so reading it after a
    // reconnect does not make the pages that follow it start over.
    if (JSON.stringify(list) !== JSON.stringify(get({ subscribe }))) store(list);
    // Keep the selection on a group the user still belongs to.
    if (get(loaded) && !list.some((g) => g.id === get(selected))) selected.set(list[0]?.id ?? null);
    if (api.acting) return;
    for (const [id, stop] of listening) {
      if (list.some((g) => g.id === id)) continue;
      stop();
      listening.delete(id);
    }
    for (const g of list) {
      if (listening.has(g.id)) continue;
      listening.set(g.id, websocket.subscribeTopic(`group:${g.id}`, (_payload, topic, type) => {
        if (topic === `group:${g.id}` && type === 'deleted') set(get({ subscribe }).filter((x) => x.id !== g.id));
      }));
    }
  }
  async function load(): Promise<Group[]> {
    const list = await api.get<Group[]>('/groups');
    loaded.set(true);
    set(list);
    return list;
  }
  // Another member signing in starts from nothing known.
  let userId: string | undefined;
  // stopOwn ends listening on the member's own topic, which says when the
  // groups they belong to changed on another page or device (spec 001 FR-110).
  let stopOwn: (() => void) | undefined;
  currentUser.subscribe((u) => {
    if (u?.id === userId) return;
    userId = u?.id;
    loaded.set(false);
    set([]);
    stopOwn?.();
    stopOwn = undefined;
    if (!u || api.acting) return;
    const own = `viewer:${u.id}`;
    stopOwn = websocket.subscribeTopic(own, (_payload, topic, type) => {
      // A failed read keeps the list; the next page load reads it again.
      if (topic === own && type === 'groups') load().catch(() => {});
    });
  });

  // Changes to the member's groups sent while the socket was down are lost,
  // so the list is read again when it comes back (001 FR-113).
  websocket.onReconnect(() => {
    if (userId && !api.acting) load().catch(() => {});
  });

  selected.subscribe((id) => {
    if (!remember) return;
    if (id) localStorage.setItem(SELECTED_KEY, id);
    else localStorage.removeItem(SELECTED_KEY);
  });

  return {
    subscribe,
    selected,
    loaded,

    load,

    async create(name: string): Promise<Group> {
      const c = await api.post<Group>('/groups', { name });
      await load();
      selected.set(c.id);
      return c;
    },
  };
}

export const groups = createGroupsStore();
